/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Dispatch, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import {
  IMessage,
  Message,
  MessageSender,
  EventAttachment,
} from '@weblite/ts-action/message'
import { CallType } from '@weblite/ts-action/media-proxy'
import { getFileBlobUrlByFileId, getFileUrl, openFile } from '@weblite/file'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { getRoom, useRoomAttribute } from '$logic/room'
import { getUser, getUserDisplayName } from '$logic/users'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  canDeleteMessage,
  getMessageByIid,
  getSenderName,
  isDownloadableMessage,
  isSharableMessage,
  searchMessages,
} from '$logic/message/service'
import { useOpenedRoomSingleMessageByIid } from '$logic/opened-room'
import { useContentFileStatus } from '$common/react-hooks/message'
import { IS_IOS } from '$func/environmentInfo'
import {
  convertExtensionToMimeType,
  getFileExtension,
  getFileTitle,
  urlToFileObject,
} from '$func/file'
import { DownloadableMessage } from '$logic/message/types'
import { shareFile } from '$func/share-api'
import { getPinnedMessageDecorator } from '$logic/pin'
import {
  formatCallDurationVerbally,
  formatScheduledCallEvent,
} from '$logic/call'
import { t } from '$i18n'
import {
  useClientUserPermissions,
  useSubscription,
} from '$logic/subscription/react-hook'
import { usePv } from '$logic/pv'
import { usePrivateSpace } from '$logic/private-space/react-hook'
import { useWappRoom } from '$logic/wapp-room/react-hook'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { DAY_MS, MINUTE_MS } from '$helper/functions/time'

export type NoUserEventAttachment = EventAttachment & { name: string }
export type SingleUserEventAttachment = EventAttachment & { userId: string }
export type DoubleUserEventAttachment = EventAttachment & {
  doerUserId: string
  affectedUserId: string
}

export function useCallEventInfo(message: Message | undefined) {
  return useMemo(() => {
    if (!message) return { uuid: undefined, canGetAttendanceReport: false }

    // const isMessageLessThanOneMonthOld =
    //   dateWithOffset() - message.date < 30 * DAY_MS

    return message.type === 'EVENT' &&
      message.attachment.event === 'CALL_ENDED' &&
      message.attachment.callType !== 'call'
      ? {
          uuid: message.attachment.uuid,
          canGetAttendanceReport:
            // isMessageLessThanOneMonthOld &&
            message.attachment.uuid,
        }
      : { uuid: undefined, canGetAttendanceReport: false }
  }, [message])
}

const getNoUserEventText = async (
  // @ts-ignore
  { event, name, messageIid, ...attachment }: NoUserEventAttachment,
  roomIsGroup: boolean,
  roomId: string,
) => {
  const message = await getMessageByIid(roomId, messageIid)
  const messageDescriptor =
    (await getPinnedMessageDecorator(message as Message)) || t('chat.aMessage')

  return event === 'CALL_STARTED'
    ? // @ts-ignore
      (attachment.callType as CallType) === 'live'
      ? t('chat.liveStarted')
      : // @ts-ignore
        (attachment.callType as CallType) === 'tribune'
        ? t('chat.tribuneStarted') // @ts-ignore
        : (attachment.callType as CallType) === 'classroom'
          ? t('chat.classStarted')
          : ''
    : event === 'CALL_ENDED'
      ? // @ts-ignore
        (attachment.callType as CallType) === 'group_call'
        ? `${t('chat.meetingEnded')} (${formatCallDurationVerbally(
            (attachment as any).duration as number,
          )})`
        : // @ts-ignore
          (attachment.callType as CallType) === 'live'
          ? `${t('chat.liveEnded')} (${formatCallDurationVerbally(
              (attachment as any).duration as number,
            )})` // @ts-ignore
          : (attachment.callType as CallType) === 'classroom'
            ? `${t('chat.classEnded')} (${formatCallDurationVerbally(
                (attachment as any).duration as number,
              )})`
            : // @ts-ignore
              (attachment.callType as CallType) === 'tribune'
              ? `${t('chat.tribuneEnded')} (${formatCallDurationVerbally(
                  (attachment as any).duration as number,
                )})`
              : ''
      : event === 'CREATE_HALL'
        ? `${roomIsGroup ? t('common.group') : t('common.channel')} ${t(
            'chat.createHall',
          )}`
        : event === 'CHANGE_HALL_IMAGE'
          ? // @ts-ignore
            !attachment.image
            ? t('chat.removeHallImage')(roomIsGroup)
            : t('chat.changeHallImage')(roomIsGroup)
          : event === 'CHANGE_HALL_NAME'
            ? t('chat.changeHallName')(roomIsGroup, name)
            : event === 'PIN_MESSAGE'
              ? t('chat.pinnedMessage')(null, messageDescriptor)
              : event === 'CALL_SCHEDULED'
                ? formatScheduledCallEvent(
                    (attachment as any).startingAt,
                    (attachment as any).callType,
                  )
                : null
}

const getSingleUserEventText = async (
  // @ts-ignore
  { userId, event, messageIid, ...attachment }: SingleUserEventAttachment,
  roomId: string,
) => {
  const user = await getUser(userId)
  if (!user) return null

  const message = await getMessageByIid(roomId, messageIid)
  const messageDescriptor =
    (await getPinnedMessageDecorator(message as Message)) || t('chat.aMessage')

  return event === 'CALL_STARTED'
    ? // @ts-ignore
      (attachment.callType as CallType) === 'group_call'
      ? `${getUserDisplayName(user)} ${t('chat.meetingStarted')}`
      : // @ts-ignore
        (attachment.callType as CallType) === 'live'
        ? t('chat.liveStarted')
        : // @ts-ignore
          (attachment.callType as CallType) === 'tribune'
          ? t('chat.tribuneStarted') // @ts-ignore
          : (attachment.callType as CallType) === 'classroom'
            ? t('chat.classStarted')
            : ''
    : event === 'JOIN_GROUP_VIA_LINK'
      ? `${t('chat.joinedGroupViaLink')(getUserDisplayName(user))}`
      : event === 'JOIN_GROUP'
        ? `${getUserDisplayName(user)} ${t('chat.joinGroup')}`
        : event === 'LEAVE_GROUP'
          ? `${getUserDisplayName(user)} ${t('chat.leaveGroup')}`
          : event === 'SIGNUP_CONTACT'
            ? `${getUserDisplayName(user)} ${t('chat.signUpContact')}`
            : event === 'PIN_MESSAGE'
              ? t('chat.pinnedMessage')(
                  getUserDisplayName(user),
                  messageDescriptor,
                )
              : event === 'CALL_SCHEDULED'
                ? formatScheduledCallEvent(
                    (attachment as any).startingAt,
                    (attachment as any).callType,
                    getUserDisplayName(user),
                  )
                : null
}

const getDoubleUserEventText = async (
  { event, doerUserId, affectedUserId }: DoubleUserEventAttachment,
  isGroup: boolean,
) => {
  const doerUser = await getUser(doerUserId)
  const affectedUser = await getUser(affectedUserId)

  if (!doerUser || !affectedUser) return null

  return event === 'ADD_TO_GROUP'
    ? t('chat.addToGroup')(
        isGroup,
        getUserDisplayName(doerUser),
        getUserDisplayName(affectedUser),
      )
    : event === 'REMOVE_FROM_GROUP'
      ? t('chat.removeFromGroup')(
          isGroup,
          getUserDisplayName(doerUser),
          getUserDisplayName(affectedUser),
        )
      : null
}

const getEventMessageText = async (message: IMessage) => {
  if (!message) return null
  const eventAttachment = message.attachment

  return (eventAttachment as SingleUserEventAttachment).userId
    ? getSingleUserEventText(
        eventAttachment as SingleUserEventAttachment,
        message.roomId,
      )
    : (eventAttachment as DoubleUserEventAttachment).doerUserId
      ? getDoubleUserEventText(
          eventAttachment as DoubleUserEventAttachment,
          (await getRoom(message.roomId))?.type === 'GROUP',
        )
      : getNoUserEventText(
          eventAttachment as NoUserEventAttachment,
          (await getRoom(message.roomId))?.type === 'GROUP',
          message.roomId,
        )
}

export function useEventMessageText(message: IMessage): {
  eventText?: string
  loading?: boolean
} {
  const { value: eventText, loading } = useAsyncFunction<string>(
    () => getEventMessageText(message),
    [message],
  )

  return { eventText, loading }
}

export function useMessageByIid(messageIid: number, roomId: string) {
  const messageInOpenedRoomStorage = useOpenedRoomSingleMessageByIid(
    roomId,
    messageIid,
  )
  const {
    loading,
    value: message,
    error,
  } = useAsyncFunction(
    () => getMessageByIid(roomId, messageIid),
    [messageIid, roomId],
  )

  return messageInOpenedRoomStorage
    ? { message: messageInOpenedRoomStorage }
    : { message, loading, error }
}

export const useMessageDownloadStatus = (message: IMessage) => {
  const { filename, fileId } = message?.attachment || {}
  const messageType = message?.type as 'FILE' | 'AUDIO' | 'VOICE' | 'IMAGE'

  const { state } = useContentFileStatus({
    fileId: message?.attachment?.fileId,
    uploadId: '',
    messageType,
  })

  if (!isDownloadableMessage(message?.type)) return { downloadable: false }

  return {
    downloadable: state === 'AVAILABLE',
    download: () => {
      console.log('download File:', {
        fileUrl: getFileUrl(fileId),
        filename,
        fileId,
      })
      openFile(getFileUrl(fileId), filename || fileId, IS_IOS)
    },
  }
}

export const useMessageShareStatus = (message: DownloadableMessage) => {
  const { value: shareableFile } = useAsyncFunction(async () => {
    const fileUrl = getFileBlobUrlByFileId(message?.attachment?.fileId)
    if (!fileUrl) return null
    // @ts-ignore
    const { filename, fileId } = message.attachment
    const fileExtension = getFileExtension(fileId)
    if (!fileExtension) return null
    const fileTitle = getFileTitle(message.type === 'IMAGE' ? fileId : filename)

    return urlToFileObject(
      fileUrl,
      `${fileTitle}.${fileExtension}`,
      convertExtensionToMimeType(fileExtension) || '',
    ) as Promise<File>
  }, [message])

  if (!isSharableMessage(message?.type))
    return { shareable: false, onShare: null }

  if (!shareableFile) return { shareable: false, onShare: null }
  return shareFile(shareableFile, message.body)
}

const debounceSearchQuery = debounce(
  (query: string, fn: Dispatch<any>) => {
    fn(query)
  },
  2000,
  {
    trailing: true,
    leading: false,
  },
)

export function useSearchMessage(roomId: string, query: string) {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(!!query)
    debounceSearchQuery(query, setDebouncedSearchTerm)
  }, [query])
  const { value } = useAsyncFunction(async () => {
    if (!debouncedSearchTerm) return { value: [] }
    analite('CHAT_SEARCH_CONTENT', { query: debouncedSearchTerm })
    return searchMessages({ roomId, debouncedSearchTerm }).finally(() =>
      setLoading(false),
    )
  }, [debouncedSearchTerm])
  return {
    loading,
    items:
      value && 'payload' in value && value.payload
        ? value.payload.messages
        : [],
    debouncedSearchTerm,
  }
}

export function useCanDeleteMessages(messages: Message[]) {
  const { value } = useAsyncFunction(async () => {
    if (!messages || !messages.length) return false
    // eslint-disable-next-line no-restricted-syntax
    for (const message of messages) {
      // eslint-disable-next-line no-await-in-loop
      if (!(await canDeleteMessage(message))) return false
    }
    return true
  }, [messages])
  return value
}

export function useSenderName(sender: MessageSender, fullName = false) {
  const { value: name, loading } = useAsyncFunction(async () => {
    return getSenderName(sender, fullName)
  }, [sender])

  return { name, loading }
}

export function useRoomStorageByRoomId(roomId: string) {
  const roomType = useRoomAttribute('type', roomId)

  const { entity: subscription } =
    useConditionalHook(roomType === 'CHANNEL' || roomType === 'GROUP', {
      hook: useSubscription,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  const { entity: pv } =
    useConditionalHook(roomType === 'PV', {
      hook: usePv,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  const privateSpace = useConditionalHook(roomType === 'PRIVATE_SPACE', {
    hook: usePrivateSpace,
    hookArgs: roomId,
    defaultValue: undefined,
  })

  const { entity: wappRoom } =
    useConditionalHook(roomType === 'PRIVATE_SPACE', {
      hook: useWappRoom,
      hookArgs: roomId,
      defaultValue: undefined,
    }) || {}

  return { subscription, pv, privateSpace, wappRoom }
}
